import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Skeleton, Button, Tag } from 'antd';
import { IoIosArrowBack } from 'react-icons/io';
import { RiCalendarEventLine, RiQrCodeLine } from 'react-icons/ri';
import { fetchEventDetail, fetchMyEvents } from '../../services/eventService';
import EventRegistrationModal from '../../components/events/EventRegistrationModal';
import EventQrModal from '../../components/events/EventQrModal';
import AuthModal from '../../components/auth/AuthModal';
import { BASE_URL } from '../../constants/Url';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr).split('T')[0].replace(/-/g, '.');
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const getSubmittedFieldLabel = (q, t) => {
  if (!q) return '';
  const title = (q.title || '').trim();
  const type = q.questionType;

  if (title === '姓名' || title.toLowerCase() === 'name') {
    return t('text_event_field_name');
  }
  if (
    title === '手机号' ||
    title === '手机号码' ||
    title === '手机' ||
    title === '电话' ||
    title === '電話' ||
    title === '電話號碼' ||
    title === '手機' ||
    title === '手機號' ||
    title.toLowerCase().includes('phone') ||
    type === 'phone'
  ) {
    return t('text_event_field_phone');
  }
  if (
    title.includes('发廊') ||
    title.includes('髮廊') ||
    title.toLowerCase().includes('salon')
  ) {
    return t('text_event_field_salon');
  }
  if (
    title.includes('公司') ||
    title.toLowerCase().includes('company')
  ) {
    return t('text_event_field_company');
  }
  if (
    title.includes('邀请码') ||
    title.includes('邀請碼') ||
    title.toLowerCase().includes('invitation')
  ) {
    return t('text_event_field_invite_code');
  }
  if (
    title === '电邮' ||
    title === '電子郵箱' ||
    title === '电子邮箱' ||
    title === '邮箱' ||
    title === '郵箱' ||
    title.toLowerCase().includes('email') ||
    type === 'email'
  ) {
    return t('text_event_field_email');
  }
  if (title === '职位' || title === '職位' || title.toLowerCase().includes('position')) {
    return t('text_event_field_position');
  }
  if (title === '城市' || title.toLowerCase().includes('city')) {
    return t('text_event_field_city');
  }
  if (title === '地址' || title.toLowerCase().includes('address')) {
    return t('text_event_field_address');
  }

  return t(title) || title;
};

export default function EventDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [userRegistration, setUserRegistration] = useState(null);

  // Modals
  const [regModalOpen, setRegModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const authModalRef = useRef();

  const auth = useSelector((state) => state.authReducer);
  const token = auth?.token;

  const currentLang = i18n.language === 'en' ? 'en' : (i18n.language === 'zh-HK' ? 'zh-HK' : 'cn');

  const handleRegisterClick = () => {
    if (!token) {
      authModalRef.current?.open();
      return;
    }
    if (!auth?.user?.completedTS) {
      authModalRef.current?.open("REGISTER");
      return;
    }
    setRegModalOpen(true);
  };

  const loadEventAndRegistration = async () => {
    setLoading(true);
    try {
      const res = await fetchEventDetail(id);
      if (res && res.status === 200 && res.data) {
        const eventData = res.data;
        setEvent(eventData);

        // If response is directly a submission record (from /events/submition/:id)
        if (eventData.approvalStatus !== undefined) {
          setUserRegistration(eventData);
        } else {
          // Check if user already registered for this event
          try {
            const myEventsRes = await fetchMyEvents({
              keyword: eventData.eventId || id,
              pageSize: 10,
            });
            if (myEventsRes && myEventsRes.data && myEventsRes.data.length > 0) {
              const found = myEventsRes.data.find(
                (r) =>
                  r.eventId === eventData.eventId ||
                  r.eventsId?._id === eventData._id ||
                  r.eventsId === eventData._id
              );
              if (found) {
                setUserRegistration(found);
              }
            }
          } catch (err) {
            console.warn('Could not check user registration status:', err);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load event detail:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadEventAndRegistration();
    }
  }, [id, currentLang]);

  const handleRegistrationSuccess = (newReg) => {
    setUserRegistration(newReg);
    // If approved right away, open QR modal
    if (newReg?.approvalStatus === 'approved' && newReg?.ticketCode) {
      setQrModalOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Top Breadcrumb Bar Placeholder */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-[1100px] mx-auto px-4 py-3.5 flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
            <div className="w-28 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>

        <div className="max-w-[1100px] mx-auto px-4 pt-6 sm:pt-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Hero Banner Skeleton */}
            <div className="w-full h-[220px] sm:h-[380px] md:h-[440px] bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse relative" />

            {/* Event Content Header Skeleton */}
            <div className="p-6 sm:p-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 pb-6 border-b border-gray-100">
                <div className="flex-1 min-w-0">
                  {/* Title Skeleton */}
                  <div className="h-7 sm:h-8 bg-gray-200 rounded-lg w-11/12 sm:w-4/5 mb-3 animate-pulse" />
                  {/* Subtitle Skeleton */}
                  <div className="h-4 bg-gray-100 rounded-md w-2/3 mb-4 animate-pulse" />

                  {/* Info Row Skeleton */}
                  <div className="flex items-center gap-3">
                    <div className="h-5 bg-gray-200 rounded-full w-44 animate-pulse" />
                    <div className="h-5 bg-gray-100 rounded-full w-24 animate-pulse" />
                  </div>
                </div>

                {/* Action Button Skeleton */}
                <div className="shrink-0">
                  <div className="h-11 w-36 rounded-xl bg-gray-200 animate-pulse" />
                </div>
              </div>

              {/* Event Description Skeleton */}
              <div className="pt-6">
                <div className="h-5 bg-gray-200 rounded-md w-28 mb-4 animate-pulse" />
                <div className="space-y-2.5">
                  <div className="h-4 bg-gray-100 rounded-md w-full animate-pulse" />
                  <div className="h-4 bg-gray-100 rounded-md w-5/6 animate-pulse" />
                  <div className="h-4 bg-gray-100 rounded-md w-3/4 animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-[1000px] mx-auto px-4 py-16 text-center">
        <h2 className="text-xl font-bold text-gray-800">{t('text_no_activities')}</h2>
        <Button onClick={() => navigate('/my-activities')} className="mt-4">
          {t('text_back_to_activities')}
        </Button>
      </div>
    );
  }

  const eventDetails = (typeof event.eventsId === 'object' && event.eventsId !== null) ? event.eventsId : event;
  const localizeObj = event.localize?.[currentLang] || event.localize?.cn || eventDetails.localize?.[currentLang] || eventDetails.localize?.cn || {};
  const title = localizeObj.title || event.title || eventDetails.title || '';
  const subtitle = localizeObj.subtitle || event.subtitle || eventDetails.subtitle || '';
  
  const images = (eventDetails.images && eventDetails.images.length > 0) ? eventDetails.images : (event.images || []);
  const bannerUrl = images.length > 0 ? (images[0]?.url || images[0]) : null;

  const startDate = eventDetails.startDate || event.startDate;
  const endDate = eventDetails.endDate || event.endDate;
  const dateRangeText = startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : (startDate ? formatDate(startDate) : '');

  const regObj = userRegistration || (event.approvalStatus !== undefined ? event : null);
  const isApproved = regObj?.approvalStatus === 'approved';
  const isPending = regObj?.approvalStatus === 'pending';
  const isCheckedIn = regObj?.isCheckedIn;

  // Submitted questionnaire questions with answers
  const submittedQuestions = (localizeObj.layout || []).filter(
    (q) => Array.isArray(q.answers) && q.answers.length > 0 && q.answers[0]
  );

  return (
    <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      {/* Top Breadcrumb Bar */}
      <div className="border-b border-gray-100">
        <div className="max-w-[1100px] mx-auto px-4 py-3.5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-black transition-colors"
          >
            <IoIosArrowBack size={18} />
            <span>{t('text_back_to_activities')}</span>
          </button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-4 pt-6 sm:pt-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Hero Banner Image */}
          {bannerUrl && (
            <div className="w-full h-[220px] sm:h-[380px] md:h-[440px] bg-gray-100 overflow-hidden relative">
              <img
                src={bannerUrl}
                alt={title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = BASE_URL + '/logo.svg';
                }}
              />
            </div>
          )}

          {/* Event Content Header */}
          <div className="p-6 sm:p-8">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 pb-6 border-b border-gray-100">
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight m-0">
                  {title}
                </h1>

                {subtitle && (
                  <p className="mt-2 text-sm sm:text-base text-gray-500 font-normal leading-relaxed">
                    {subtitle}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-600">
                  <div className="flex items-center gap-1.5 font-medium">
                    <RiCalendarEventLine className="text-[#e50a47]" size={18} />
                    <span>{t('text_event_time')}{dateRangeText}</span>
                  </div>

                  {event.eventId && (
                    <span className="bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full text-xs font-mono font-medium">
                      {event.eventId}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons & Status Badge */}
              <div className="flex flex-col sm:flex-row md:flex-col items-start md:items-end gap-3 shrink-0">
                {userRegistration ? (
                  <div className="flex flex-col items-start md:items-end gap-2 w-full sm:w-auto">
                    {/* Status Badge */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-medium">{t('text_registered')}:</span>
                      {isCheckedIn ? (
                        <Tag color="default">{t('text_status_checked_in')}</Tag>
                      ) : isApproved ? (
                        <Tag color="success">{t('text_status_approved')}</Tag>
                      ) : isPending ? (
                        <Tag color="warning">{t('text_status_pending')}</Tag>
                      ) : (
                        <Tag color="error">{t('text_status_rejected')}</Tag>
                      )}
                    </div>

                    {/* View Ticket Button if Approved */}
                    {isApproved && (
                      <Button
                        type="primary"
                        icon={<RiQrCodeLine size={18} />}
                        onClick={() => setQrModalOpen(true)}
                        size="large"
                        style={{
                          borderRadius: 10,
                          backgroundColor: '#059669',
                          borderColor: '#059669',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        {t('text_view_ticket')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    type="primary"
                    size="large"
                    onClick={handleRegisterClick}
                    style={{
                      borderRadius: 10,
                      backgroundColor: '#e50a47',
                      borderColor: '#e50a47',
                      fontWeight: 600,
                      height: 46,
                      padding: '0 28px',
                      boxShadow: '0 4px 12px rgba(229, 10, 71, 0.3)',
                    }}
                  >
                    {t('text_register_now')}
                  </Button>
                )}
              </div>
            </div>

            {/* Event Description / Agenda / Details */}
            <div className="pt-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3">
                {t('text_event_details')}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {subtitle || title}
              </p>
            </div>

            {/* Submitted Questionnaire Answers (if user already registered) */}
            {submittedQuestions.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">
                  {t('text_submitted_info') || '已提交报名信息'}
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {submittedQuestions.map((q) => (
                    <div key={q._id || q.title} className="flex flex-col gap-1">
                      <span className="text-xs text-gray-400 font-medium">
                        {getSubmittedFieldLabel(q, t)}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {q.questionType === 'phone' && q.defaultCountryCode ? `${q.defaultCountryCode} ` : ''}
                        {q.answers.join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Registration Questionnaire Modal ── */}
      <EventRegistrationModal
        open={regModalOpen}
        onClose={() => setRegModalOpen(false)}
        event={event}
        onSuccess={handleRegistrationSuccess}
      />

      {/* ── Admission QR Ticket Modal ── */}
      <EventQrModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        event={regObj || event}
      />

      {/* ── Login / Register Modal for Guests ── */}
      <AuthModal
        ref={authModalRef}
        onAuthSuccess={() => {
          setRegModalOpen(true);
        }}
      />
    </div>
  );
}
